import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button/standard-button';
import { ModalBody, ModalFooter, ModalHeader } from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import Content from '../../common/components/configuration/view.tsx';
import type { DefaultAssignee, User, EditableComponent } from '../../common/model/types.tsx';
import messages from './messages.tsx';

export type Props = {
	isSaving: boolean;
	canSubmit: boolean;
	name: string;
	description: string | undefined;
	lead?: User;
	defaultAssignee: DefaultAssignee;
	baseUrl: string;
	projectDefaultAssignee: DefaultAssignee | undefined;
	projectLeadName: string;
	nameErrorMessage: string;
	onConfirm: () => void;
	onCancel: () => void;
	onUpdateComponent: (arg1: EditableComponent) => void;
};

type PropsWithIntl = Props & {
	intl: IntlShape;
};

type HeaderProps = {
	intl: IntlShape;
};

type FooterProps = {
	isSaving: boolean;
	canSubmit: boolean;
	onConfirm: () => void;
	onCancel: () => void;
	intl: IntlShape;
};

const Header = ({ intl: { formatMessage } }: HeaderProps) => (
	// eslint-disable-next-line @atlaskit/design-system/use-heading
	<h2>{formatMessage(messages.globalComponentsHeader)}</h2>
);

const Footer = ({
	intl: { formatMessage },
	isSaving,
	canSubmit,
	onConfirm,
	onCancel,
}: FooterProps) => (
	<ModalFooter>
		<ButtonsContainer>
			{isSaving && (
				<Box xcss={spinnerContainerStyles}>
					<Spinner size="small" />
				</Box>
			)}
			<Button
				appearance="primary"
				type="submit"
				onClick={onConfirm}
				isDisabled={!canSubmit}
				// @ts-expect-error - TS2322 - Type '{ children: string; appearance: "primary"; type: "submit"; onClick: () => void; isDisabled: boolean; externalId: string; }' is not assignable to type 'IntrinsicAttributes & ButtonProps & RefAttributes<HTMLElement>'.
				externalId="project-components.item.add"
			>
				{formatMessage(messages.saveButton)}
			</Button>
			<Button appearance="subtle-link" onClick={onCancel} isDisabled={isSaving}>
				{formatMessage(messages.cancelButton)}
			</Button>
		</ButtonsContainer>
	</ModalFooter>
);

function CreateModal(props: PropsWithIntl) {
	const {
		baseUrl,
		projectDefaultAssignee,
		projectLeadName,
		isSaving,
		canSubmit,
		name,
		description,
		lead,
		defaultAssignee,
		intl,
		onConfirm,
		onCancel,
		onUpdateComponent,
		nameErrorMessage,
	} = props;

	// Unstable components cause bugs and performance problems - please fix this! go/unstable-components
	// eslint-disable-next-line react/no-unstable-nested-components
	const HeaderComponent = () => <Header intl={intl} />;
	// Unstable components cause bugs and performance problems - please fix this! go/unstable-components
	// eslint-disable-next-line react/no-unstable-nested-components
	const FooterComponent = () => (
		<Footer
			intl={intl}
			isSaving={isSaving}
			canSubmit={canSubmit}
			onConfirm={onConfirm}
			onCancel={onCancel}
		/>
	);

	return (
		<ShortcutScope>
			<ModalDialog
				autoFocus
				onClose={onCancel}
				width="medium"
				messageId="project-directories.components.create-modal.modal-dialog"
				messageType="transactional"
			>
				<ModalHeader>
					<HeaderComponent />
				</ModalHeader>
				<ModalBody>
					<Content
						baseUrl={baseUrl}
						onUpdateComponent={onUpdateComponent}
						projectDefaultAssignee={projectDefaultAssignee}
						projectLeadName={projectLeadName}
						name={name}
						description={description}
						lead={lead}
						defaultAssignee={defaultAssignee}
						nameErrorMessage={nameErrorMessage}
					/>
				</ModalBody>
				<FooterComponent />
			</ModalDialog>
		</ShortcutScope>
	);
}

export default injectIntl(CreateModal);

const spinnerContainerStyles = xcss({
	marginRight: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonsContainer = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
});
